import { faker } from "@faker-js/faker";
import type { OpenAPIV3 } from "openapi-types";

type IdRegistry = Map<string, Array<string | number>>;

export interface GenerationOptions {
  /**
   * Custom generators by field name.
   */
  generators?: Record<string, () => unknown>;
  /**
   * Shared ID registry for relationship consistency.
   */
  registry?: IdRegistry;
}

interface GenerationContext {
  fieldName?: string;
  parentName?: string;
  generators?: Record<string, () => unknown>;
  registry?: IdRegistry;
}

const defaultRegistry: IdRegistry = new Map();

export function generateMockData(
  schema?: OpenAPIV3.SchemaObject,
  options: GenerationOptions = {}
): unknown {
  const registry = options.registry ?? defaultRegistry;
  return innerGenerate(schema, {
    generators: options.generators,
    registry,
  });
}

function innerGenerate(
  schema?: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): unknown {
  if (!schema) {
    return null;
  }

  // Handle enums
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return faker.helpers.arrayElement(schema.enum as any[]);
  }

  const type = schema.type;

  if (Array.isArray(type)) {
    // Prefer string, number, object, boolean in that order
    const preferred = [
      "string",
      "number",
      "integer",
      "object",
      "array",
      "boolean",
    ] as const;
    const chosen = preferred.find((t) => type.includes(t as any)) ?? type[0];
    return innerGenerate({ ...schema, type: chosen } as any, ctx);
  }

  switch (type) {
    case "string":
      return generateString(schema, ctx);
    case "number":
      return generateNumber(schema, ctx);
    case "integer":
      return generateInteger(schema, ctx);
    case "boolean":
      return faker.datatype.boolean();
    case "array":
      return generateArray(schema, ctx);
    case "object":
    default:
      return generateObject(schema, ctx);
  }
}

function generateString(
  schema: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): string {
  const format = schema.format || "";
  const nameSource = (
    ctx?.fieldName ??
    schema.title ??
    schema.description ??
    ""
  )
    .toString()
    .toLowerCase();

  if (schema.example && typeof schema.example === "string") {
    return schema.example;
  }

  // Custom generators from config
  if (ctx?.fieldName && ctx.generators && ctx.generators[ctx.fieldName]) {
    const gen = ctx.generators[ctx.fieldName];
    return String(gen());
  }

  // Common smart mappings via explicit faker path (non-standard extension)
  const xFaker = (schema as any)["x-faker"];
  if (xFaker) {
    const path = String(xFaker);
    const [ns, method] = path.split(".");
    const fakerNs: any = (faker as any)[ns];
    if (fakerNs && typeof fakerNs[method] === "function") {
      return fakerNs[method]();
    }
  }

  // Format-based
  if (format === "email" || /email/.test(nameSource)) {
    return faker.internet.email();
  }
  if (format === "uuid" || /uuid/.test(nameSource)) {
    return faker.string.uuid();
  }
  if (format === "date-time") {
    return faker.date.recent().toISOString();
  }
  if (format === "date") {
    return faker.date.recent().toISOString().split("T")[0]!;
  }
  if (format === "uri" || format === "url" || /url/.test(nameSource)) {
    return faker.internet.url();
  }

  // Name-like
  if (/^(first[-_]?name|fname)$/.test(nameSource)) {
    return faker.person.firstName();
  }
  if (/^(last[-_]?name|lname)$/.test(nameSource)) {
    return faker.person.lastName();
  }
  if (/name/.test(nameSource)) {
    return faker.person.fullName();
  }

  // Phone-like
  if (/phone|mobile/.test(nameSource)) {
    return faker.phone.number();
  }

  // Address-like
  if (/address/.test(nameSource)) {
    return faker.location.streetAddress();
  }
  if (/street/.test(nameSource)) {
    return faker.location.street();
  }
  if (/city/.test(nameSource)) {
    return faker.location.city();
  }
  if (/country/.test(nameSource)) {
    return faker.location.country();
  }

  // Date-like
  if (
    /createdat|updatedat|timestamp|date/.test(
      nameSource.replace(/[_\s]/g, "").toLowerCase()
    )
  ) {
    return faker.date.recent().toISOString();
  }

  // Status-like
  if (nameSource === "status") {
    return faker.helpers.arrayElement(["active", "pending", "completed"]);
  }

  const minLength = schema.minLength ?? 5;
  const maxLength = schema.maxLength ?? 20;
  const targetLength = faker.number.int({ min: minLength, max: maxLength });

  let text = faker.lorem.words(Math.max(1, Math.floor(targetLength / 5)));
  // Ensure string respects minLength/maxLength constraints
  if (text.length < minLength) {
    while (text.length < minLength) {
      text += " " + faker.lorem.word();
    }
  }
  if (text.length > maxLength) {
    text = text.slice(0, maxLength);
  }

  return text;
}

function generateNumber(
  schema: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): number {
  const nameSource = (ctx?.fieldName ?? "").toString().toLowerCase();
  const min = (schema.minimum as number | undefined) ?? 0;
  const max = (schema.maximum as number | undefined) ?? 1000;

  // Price-like
  if (/price|amount|cost|salary/.test(nameSource)) {
    const value = faker.number.float({
      min: min || 0,
      max: max || 10000,
    });
    return Math.round(value * 100) / 100;
  }

  const value = faker.number.float({ min, max });
  return value;
}

function generateInteger(
  schema: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): number {
  const nameSource = (ctx?.fieldName ?? "").toString().toLowerCase();
  const min = (schema.minimum as number | undefined) ?? 0;
  const max = (schema.maximum as number | undefined) ?? 1000;

  // Integer IDs with registry linkage
  if (/id$|_id$/.test(nameSource)) {
    const base = nameSource.replace(/(_id|id)$/i, "") || "id";
    const reg = ctx?.registry;
    if (!reg) {
      return faker.number.int({ min, max });
    }

    // Foreign key: if base has known IDs, reuse one
    const existing = reg.get(base);
    if (existing && existing.length > 0 && nameSource !== "id") {
      return Number(faker.helpers.arrayElement(existing));
    }

    const value = faker.number.int({ min, max });
    const list = reg.get(base) ?? [];
    list.push(value);
    reg.set(base, list);
    return value;
  }

  return faker.number.int({ min, max });
}

function generateArray(
  schema: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): unknown[] {
  if (!("items" in schema)) {
    return [];
  }
  const items = (schema as OpenAPIV3.ArraySchemaObject).items as
    | OpenAPIV3.SchemaObject
    | undefined;
  const minItems = schema.minItems ?? 1;
  const maxItems = schema.maxItems ?? Math.max(minItems, 5);
  const length = faker.number.int({ min: minItems, max: maxItems });

  const result: unknown[] = [];
  for (let i = 0; i < length; i++) {
    result.push(
      innerGenerate(
        items,
        ctx && { ...ctx, registry: ctx.registry ?? defaultRegistry }
      )
    );
  }
  return result;
}

function generateObject(
  schema: OpenAPIV3.SchemaObject,
  ctx?: GenerationContext
): Record<string, unknown> {
  const obj: Record<string, unknown> = {};

  const properties = schema.properties || {};
  const required = schema.required || [];

  for (const [key, propSchema] of Object.entries(properties)) {
    const childCtx: GenerationContext = {
      ...ctx,
      registry: ctx?.registry ?? defaultRegistry,
      fieldName: key,
    };

    obj[key] = innerGenerate(propSchema as OpenAPIV3.SchemaObject, childCtx);
  }

  return obj;
}
